import React from 'react';
import classNames from 'classnames';
import { Notification, NotificationType } from 'Douleutaras/types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'Douleutaras/redux/hooks';
import { markNotificationAsRead } from 'Douleutaras/redux/data/notifications/notificationsSlice';
import { buildUrl, getAbsoluteUrl, VALID_URLS } from 'Douleutaras/utilities/url-builder';
import { humanReadableHoursFormat } from 'Douleutaras/utilities/formatting/date.utils';
import { URLS } from 'Douleutaras/constants/urls';

import './NotificationItem.scss';

export interface NotificationItemProps {
  id: string;
  notification: Notification;
  onClick?: Function;
  isCenter?: boolean;
}

export const NotificationItem = ({ notification, onClick, id, isCenter }: NotificationItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  let btnText = '';
  let icon = '';

  switch (notification.notificationType) {
    case NotificationType.JOB_ASSIGNED:
      btnText = t('T.NC View Job');
      break;
    case NotificationType.INVOICE_NEW:
      btnText = t('T.NC Pay now');
      break;
    case NotificationType.PAYMENT_ACCEPTANCE:
      btnText = t('T.NC View');
      icon = 'credit-card';
      break;
    case NotificationType.PAYMENT_CUSTOMER_DECLINED:
      btnText = t('T.NC Contact customer');
      icon = 'credit-card';
      break;
    case NotificationType.PAYMENT_CANCELLED_AUTOMATICALLY:
      icon = 'credit-card';
      break;
    case NotificationType.BONUS_JOBS_TARGET_MET_REVENUE_PENDING:
    case NotificationType.BONUS_MISSED:
      btnText = t('T.NC My jobs list');
      icon = 'coins';
      break;
    case NotificationType.BONUS_NEW_MONTH_NEW_TARGET:
    case NotificationType.BONUS_UNLOCKED:
      btnText = t('T.NC Bonus page');
      icon = 'coins';
      break;
    default:
  }

  const makeNotificationRead = async () => {
    const callback = () => {
      switch (notification.notificationType) {
        case NotificationType.JOB_ASSIGNED:
        case NotificationType.JOB_CANCELLED:
        case NotificationType.JOB_MEETING:
        case NotificationType.PAYMENT_CANCELLED_AUTOMATICALLY:
          if (notification.bidId) {
            navigate(buildUrl(VALID_URLS.PROS_JOB, notification.bidId));
          } else {
            // @TODO: handle edge case when bidId is null.
          }
          break;
        case NotificationType.INVOICE_NEW:
          navigate(getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.TRANSACTIONS.INVOICES'));
          break;
        case NotificationType.CUSTOMER_REVIEW:
          navigate(getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.REVIEWS'));
          break;
        case NotificationType.DAILY:
          navigate(getAbsoluteUrl('PRO_DASHBOARD.DAILY_SUMMARY'));
          break;
        case NotificationType.WEEKLY:
          navigate(getAbsoluteUrl('PRO_DASHBOARD.WEEKLY_SUMMARY'));
          break;
        case NotificationType.PAYMENT_ACCEPTANCE:
          // TODO: Second parameter
          navigate(buildUrl(VALID_URLS.CUSTOMER_ONLINE_PAYMENT, notification.bidId, notification.objectId));
          break;
        case NotificationType.PAYMENT_CUSTOMER_DECLINED:
          navigate(buildUrl(VALID_URLS.CONTACT_CUSTOMER, notification.bidId));
          break;
        case NotificationType.BONUS_JOBS_TARGET_MET_REVENUE_PENDING:
        case NotificationType.BONUS_MISSED:
          navigate(URLS.SELLER.MY_JOBS);
          break;
        case NotificationType.BONUS_NEW_MONTH_NEW_TARGET:
        case NotificationType.BONUS_UNLOCKED:
          navigate(getAbsoluteUrl('PRO_DASHBOARD.BONUS'));
          break;
        default:
      }
    };

    await dispatch(markNotificationAsRead({ notificationId: id }));
    callback();
  };

  return (
    <a href="#" className="gtm-notifications_center-notification" styleName={classNames('notice-item', {
      'notice-item--center': isCenter,
      'notice-item--special': notification.isSpecial,
      'notice-item--dotted': !notification.isRead
    })} onClick={e => { e.preventDefault(); makeNotificationRead(); onClick && onClick(); }}>
      <div styleName="notice-item__icon" className={`dashicon dashicon-${notification.icon || icon || notification.notificationType}`} >
        { notification.isSpecial &&
        <div styleName="notice-item__icon-star" className="dashicon dashicon-stars" />
        }
      </div>
      <div styleName="notice-item__content">
        <div styleName="notice-item__title">{notification.message ? notification.message.title : ''}</div>
        <div styleName="notice-item__caption">{notification.message ? notification.message.body : ''}</div>
        <div styleName="notice-item__footer">
          <div styleName="notice-item__link">{btnText}</div>
          <div styleName="notice-item__time">
            {humanReadableHoursFormat(notification.created, t)}
          </div>
        </div>
      </div>
    </a>
  );
};
